import React, { useEffect, useState } from 'react'
import { MenuItem, Position, Checkbox, Menu } from '@blueprintjs/core'
import { Select2, ItemListPredicate } from '@blueprintjs/select'
import { Button, Flex, HighlightedText } from '@ui'
import { IUIButtonProps } from '@ui/buttons/Button'
import fuzzy from 'fuzzaldrin-plus'
import { get } from 'lodash'
import ClearableSelectWrapper from './ClearableSelectWrapper'

interface SelectProps<T> {
  btnText?: React.ReactNode
  value: T | null
  onChange: (param: T) => void
  onCreate?: (param: string) => void
  buttonProps?: IUIButtonProps
  itemDisabled?: (param: T) => boolean
  data: T[]
  filterable?: boolean
  canCreate?: boolean
  minPopoverWidth?: number
  minItemWidth?: number
  itemListPredicate?: (query: string, data: T[]) => T[]
}

const BPSelect = Select2

export default function Select<T extends { id: string; name: string }>({
  btnText,
  value,
  onChange,
  buttonProps,
  onCreate,
  data,
  canCreate,
  filterable = true,
  minPopoverWidth = 313,
  minItemWidth = 316,
  itemListPredicate = (query: string, params: T[]) => {
    return query ? fuzzy.filter(params, query, { key: 'name' as never }) : params
  },
}: SelectProps<T>) {
  const [query, setQuery] = useState('')
  const NoResults =
    query && canCreate ? (
      <MenuItem
        style={{ minWidth: 316 }}
        onClick={() => {
          onCreate?.(query as any)
        }}
        text={`Create option ${query || ''}`}
      />
    ) : (
      <MenuItem disabled style={{ minWidth: minItemWidth }} text="No results" />
    )

  return (
    <ClearableSelectWrapper>
      <BPSelect
        filterable={filterable}
        resetOnSelect
        popoverProps={{
          minimal: true,
          position: Position.BOTTOM,
        }}
        noResults={NoResults}
        items={data}
        onQueryChange={(query) => setQuery(query)}
        itemRenderer={(data, { handleClick, modifiers, query }) =>
          modifiers.matchesPredicate ? (
            <div style={{ paddingTop: 5, minWidth: minPopoverWidth }}>
              <Menu>
                <MenuItem
                  active={modifiers.active}
                  key={data.id}
                  onClick={(e: React.MouseEvent<HTMLElement>) => {
                    handleClick(e)
                  }}
                  text={
                    <Flex align="center">
                      <HighlightedText text={data.name} query={query} />
                    </Flex>
                  }
                />
              </Menu>
            </div>
          ) : null
        }
        itemListPredicate={itemListPredicate}
        onItemSelect={onChange}>
        <Button
          {...buttonProps}
          rightIconName={!get(buttonProps, 'iconName') && !value ? 'chevron-down' : undefined}
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            ...buttonProps?.style,
          }}
          text={value ? value.name : btnText || 'Select'}
        />
      </BPSelect>
      <div />
    </ClearableSelectWrapper>
  )
}
